import { Ref, ref, watch } from 'vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import ISelectable from '@/types/Atomic/ISelectable';

interface ILettrFolder {
    id: number;
    name: string;
    templates_count: number;
}

/**
 * Lettr keeps a separate set of folders per module, so the list is refetched
 * whenever the purpose changes and the first folder is preselected - the same
 * one Lettr falls back to on its own when none is given.
 */
export const useLettrFolders = (
    integrationId: Ref<number | null>,
    purpose: Ref<string>
) => {
    const folders = ref<ISelectable[]>([]);
    const folderId = ref<string | number>('');
    const loading = ref(false);
    const error = ref('');

    const load = async () => {
        folders.value = [];
        folderId.value = '';
        error.value = '';

        if (!integrationId.value) {
            return;
        }

        loading.value = true;

        try {
            const response = await axios.get(
                route('integrations.lettr.folders.index', {
                    integration: integrationId.value,
                }),
                { params: { purpose: purpose.value } }
            );

            folders.value = (response.data.folders as ILettrFolder[]).map(
                (folder) => ({ title: folder.name, value: folder.id })
            );

            if (folders.value.length > 0) {
                folderId.value = folders.value[0].value;
            }
        } catch (e) {
            error.value =
                'Could not load folders from Lettr. Templates go to the first folder of the module.';
        } finally {
            loading.value = false;
        }
    };

    watch([integrationId, purpose], load);

    return { folders, folderId, loading, error, load };
};

export default useLettrFolders;
